import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input
} from '@angular/core';
import { SystemTag, TagOwnershipType } from '@ui/shared/model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ImmomioTooltipDirective } from '@immomio/ui/tooltip';
import { UpperCasePipe } from '@angular/common';
import { ThemeService } from '../../../infrastructure/theme/theme.service';
import { TAG_CONFIGS, TagConfig } from './tag-config';
import { getImmomioTagColors, ImmomioTagColor } from './immomio-tag-color';

@Component({
  selector: 'app-tag',
  templateUrl: './tag.component.html',
  styleUrls: ['./tag.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ImmomioTooltipDirective, TranslatePipe, UpperCasePipe]
})
export class TagComponent {
  private translate = inject(TranslateService);
  private themeService = inject(ThemeService);

  readonly name = input<string>(undefined);
  readonly isSystemTag = input<boolean>(undefined);
  readonly tagType = input<TagOwnershipType>(undefined);
  readonly disableTooltip = input<boolean>(undefined);
  readonly nowrap = input<boolean>(undefined);
  readonly color = input<string | null>(undefined);

  protected readonly TRUNCATE_LENGTH = 15;

  readonly tagConfig = computed<TagConfig>(() => {
    let config: ImmomioTagColor;
    const tagType = this.tagType();
    if (this.isSystemTag()) {
      config = this.name() as SystemTag | TagOwnershipType;
    } else if (tagType === TagOwnershipType.PERSONAL) {
      config = TagOwnershipType.PERSONAL;
    } else if (tagType === TagOwnershipType.DYNAMIC) {
      if (this.name()?.startsWith('PROJEKT-')) {
        config = 'project';
      } else {
        config = TagOwnershipType.DYNAMIC;
      }
    } else {
      config = TagOwnershipType.SHARED;
    }
    return TAG_CONFIGS[config];
  });

  readonly backgroundColor = computed<string | null>(() => {
    if (this.color()) {
      return this.color();
    }

    const currentTheme = this.themeService.getActiveTheme;
    const tagColors = getImmomioTagColors(currentTheme.properties);
    const tagConfig = this.tagConfig();

    return tagColors[tagConfig.color];
  });

  readonly textColor = computed<string>(() => {
    const bgColor = this.backgroundColor();
    if (!bgColor) return '#ffffff'; // Default
    return this.shouldUseDarkText(bgColor) ? '#000000' : '#ffffff';
  });

  private shouldUseDarkText(color: string): boolean {
    // Match #RRGGBB or #RRGGBBAA
    const match = color.match(/^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/);
    if (!match) return false;

    const hex = match[1];
    const alphaHex = match[2];

    // Parse RGB
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Parse alpha if present, otherwise default to 1
    const a = alphaHex ? parseInt(alphaHex, 16) / 255 : 1;

    // If you want to apply alpha blending over white background
    const blendedR = Math.round(r * a + 255 * (1 - a));
    const blendedG = Math.round(g * a + 255 * (1 - a));
    const blendedB = Math.round(b * a + 255 * (1 - a));

    const bgLuminance = this.getRelativeLuminance(blendedR, blendedG, blendedB);

    // Contrast with black and white
    const contrastWithWhite = (1 + 0.05) / (bgLuminance + 0.05);
    const contrastWithBlack = (bgLuminance + 0.05) / (0 + 0.05);

    return contrastWithBlack > contrastWithWhite;
  }

  private getRelativeLuminance(r: number, g: number, b: number): number {
    // Normalize to 0-1
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    // Apply gamma correction (WCAG 2.0 formula)
    const rLinear =
      rNorm <= 0.03928 ? rNorm / 12.92 : Math.pow((rNorm + 0.055) / 1.055, 2.4);
    const gLinear =
      gNorm <= 0.03928 ? gNorm / 12.92 : Math.pow((gNorm + 0.055) / 1.055, 2.4);
    const bLinear =
      bNorm <= 0.03928 ? bNorm / 12.92 : Math.pow((bNorm + 0.055) / 1.055, 2.4);

    // Calculate relative luminance
    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
  }

  readonly truncate = computed<boolean>(() => {
    const label = this.name() || this.tagConfig()?.label;
    return (label?.length ?? 0) > this.TRUNCATE_LENGTH;
  });

  readonly tooltip = computed<string>(() => {
    if (this.truncate()) {
      return this.name() || this.tagConfig()?.label || '';
    }
    const configTooltip = this.tagConfig()?.tooltip;
    return configTooltip ? String(this.translate.instant(configTooltip)) : '';
  });
}
